import _ from 'lodash'
import QueryString from 'qs'
import { Helmet as HelmetOriginal } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { generatePath } from 'react-router'

import { useAppConfig } from '../../hooks/useAppConfig'
import { useCurrentRoute } from '../../hooks/useCurrentRoute'
import { getPublicUrl } from '../../redux/config'
import { formatUrl, parseGetParams } from '../../utils/routes'

type Props = {
  seo?: {
    metaTitle?: string
    metaDescription?: string
    keywords?: string[]
  }
  hreflang?: boolean
  robots?: string
  noIndex?: boolean
}

function formatFullyQualifiedUrl(
  publicUrl: string,
  lang: string,
  routeDef: string,
  routeParams: any,
  queryParams: QueryString.ParsedQs
) {
  const routePath = generatePath(routeDef, routeParams || {})
  return formatUrl(publicUrl, `/${lang}${routePath}`, queryParams)
}

export const Helmet: React.FC<Props> = ({ children, seo, hreflang, robots, noIndex }) => {
  const { t } = useTranslation(['routes'])
  const { availableLanguages, primaryLanguage } = useAppConfig()
  const { route, location } = useCurrentRoute()
  const queryParams = parseGetParams(location.search)
  const publicUrl = useSelector(getPublicUrl)

  const alternateUrls = _.map(hreflang ? availableLanguages : [], lang => ({
    lang,
    url: formatFullyQualifiedUrl(
      publicUrl,
      lang,
      t(`routes:${route?.key}`, { lng: lang }),
      route?.routeData?.params,
      queryParams
    )
  }))

  const canonicalUrl = hreflang
    ? formatFullyQualifiedUrl(
        publicUrl,
        primaryLanguage,
        t(`routes:${route?.key}`, { lng: primaryLanguage }),
        route?.routeData?.params,
        queryParams
      )
    : ''

  return (
    <HelmetOriginal>
      {seo?.metaTitle && <title>{seo?.metaTitle}</title>}
      {seo?.metaDescription && <meta name="description" content={seo?.metaDescription} />}
      {!_.isEmpty(seo?.keywords) && <meta name="keywords" content={_.join(seo?.keywords, ',')} />}
      {!_.isEmpty(robots) && <meta name="robots" content={robots} />}
      {noIndex && <meta name="robots" content="noindex" />}

      {!_.isEmpty(canonicalUrl) && <link rel="canonical" href={canonicalUrl} />}
      {_.size(alternateUrls) > 0 && <link rel="alternate" hrefLang="x-default" href={alternateUrls[0].url} />}
      {_.map(alternateUrls, alt => (
        <link key={alt.lang} rel="alternate" hrefLang={alt.lang} href={alt.url} />
      ))}

      {children}
    </HelmetOriginal>
  )
}
